/** Geodesic helpers for the Pathfinder / COP measure tool. Points are [lon, lat]. */

export type MeasureLngLat = [number, number];

const EARTH_KM = 6371;

const toRad = (deg: number) => (deg * Math.PI) / 180;
const toDeg = (rad: number) => (rad * 180) / Math.PI;

export function haversineKm(a: MeasureLngLat, b: MeasureLngLat): number {
  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const sinLat = Math.sin(dLat / 2);
  const sinLon = Math.sin(dLon / 2);
  const h =
    sinLat * sinLat +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * sinLon * sinLon;
  return EARTH_KM * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export function pathLengthKm(points: MeasureLngLat[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += haversineKm(points[i - 1], points[i]);
  return total;
}

/** Closed-ring perimeter (path plus the last→first edge). */
export function circumferenceKm(points: MeasureLngLat[]): number {
  if (points.length < 3) return 0;
  return pathLengthKm(points) + haversineKm(points[points.length - 1], points[0]);
}

/**
 * Spherical polygon area (km²). Uses the authalic trapezoid used by most
 * web GIS stacks; good enough for operational range / site sketches.
 */
export function polygonAreaKm2(points: MeasureLngLat[]): number {
  if (points.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const [lon1, lat1] = points[i];
    const [lon2, lat2] = points[(i + 1) % points.length];
    sum += toRad(lon2 - lon1) * (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
  }
  return Math.abs((sum * EARTH_KM * EARTH_KM) / 2);
}

/** Destination point given start, bearing (deg) and distance (km). */
export function destination(start: MeasureLngLat, bearingDeg: number, distKm: number): MeasureLngLat {
  const δ = distKm / EARTH_KM;
  const θ = toRad(bearingDeg);
  const φ1 = toRad(start[1]);
  const λ1 = toRad(start[0]);
  const sinφ2 = Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ);
  const φ2 = Math.asin(Math.min(1, Math.max(-1, sinφ2)));
  const λ2 =
    λ1 +
    Math.atan2(
      Math.sin(θ) * Math.sin(δ) * Math.cos(φ1),
      Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2),
    );
  return [toDeg(λ2), toDeg(φ2)];
}

/** Closed geodesic ring around `center` with radius `radiusKm`. */
export function geodesicCircle(center: MeasureLngLat, radiusKm: number, steps = 64): MeasureLngLat[] {
  if (!(radiusKm > 0)) return [];
  const ring: MeasureLngLat[] = [];
  for (let i = 0; i <= steps; i++) {
    ring.push(destination(center, (i * 360) / steps, radiusKm));
  }
  return ring;
}

export function formatDistanceKm(km: number): string {
  if (!(km > 0)) return '—';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(2)} km (${(km * 0.621371).toFixed(2)} mi)`;
}

export function formatAreaKm2(km2: number): string {
  if (!(km2 > 0)) return '—';
  const m2 = km2 * 1_000_000;
  if (m2 < 10_000) return `${Math.round(m2)} m²`;
  return `${km2.toFixed(2)} km² (${(km2 * 0.386102).toFixed(2)} mi²)`;
}

export type MeasureMode = 'rings' | 'distance' | 'area' | 'radius' | 'bearing' | 'coords';

/** Forward azimuth in degrees, 0–360, clockwise from true north. */
export function bearingDeg(a: MeasureLngLat, b: MeasureLngLat): number {
  const φ1 = toRad(a[1]);
  const φ2 = toRad(b[1]);
  const Δλ = toRad(b[0] - a[0]);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

const CARDINALS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

export function formatBearing(deg: number): string {
  const cardinal = CARDINALS[Math.round(deg / 45) % 8];
  return `${deg.toFixed(1)}° ${cardinal}`;
}

/** Rings and coordinates keep one point. Radius and bearing keep a start and a movable end. */
export function nextMeasurePoints(
  mode: MeasureMode,
  prev: MeasureLngLat[],
  next: MeasureLngLat,
): MeasureLngLat[] {
  if (mode === 'rings' || mode === 'coords') return [next];
  if (mode === 'radius' || mode === 'bearing') {
    return prev.length === 0 ? [next] : [prev[0], next];
  }
  return [...prev, next];
}

/** Spacing of the first ring, in metres. Further rings are multiples of this. */
export const MEASURE_RING_PRESETS_M = [25, 50, 100, 250, 500, 1000] as const;

export const MEASURE_RING_COUNT_MAX = 20;

/** Concentric radii: spacing, 2×spacing, … count×spacing. */
export function concentricRadiiKm(spacingM: number, count: number): number[] {
  const step = spacingM / 1000;
  const n = Math.max(0, Math.min(MEASURE_RING_COUNT_MAX, Math.floor(count)));
  if (!(step > 0) || n < 1) return [];
  return Array.from({ length: n }, (_, i) => step * (i + 1));
}
